import { datadogRum } from '@datadog/browser-rum';
import { DateRange } from '../types';

interface VitalReference {
  stop: () => void;
}

export class GeneralLedgerPerformanceTracker {
  private activeVitals: Map<string, any> = new Map();

  private calculateDateRangeMonths(dateRange: DateRange): number {
    if (!dateRange.startDate || !dateRange.endDate) return 0;

    const start = new Date(dateRange.startDate);
    const end = new Date(dateRange.endDate);

    return Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24 * 30.44));
  }

  private getDateRangeCategory(months: number): string {
    if (months <= 1) return 'single_month';
    if (months <= 3) return 'quarter';
    if (months <= 6) return 'half_year';
    if (months <= 12) return 'full_year';
    return 'multi_year';
  }

  private buildContext(
    dateRange?: DateRange,
    clientId?: string,
    dataSize?: number,
  ): Record<string, any> {
    const context: Record<string, any> = {};

    if (clientId) {
      context.clientId = clientId;
    }

    if (dateRange) {
      const months = this.calculateDateRangeMonths(dateRange);
      context.dateRangeMonths = months;
      context.dateRangeCategory = this.getDateRangeCategory(months);
      context.startDate = dateRange.startDate;
      context.endDate = dateRange.endDate;
    }

    if (dataSize !== undefined) {
      context.dataSize = dataSize;
    }

    return context;
  }

  pageLoadStart(clientId?: string): void {
    const reference = datadogRum.startDurationVital('generalLedgerPageLoad', {
      description: 'full_page_load',
      context: { clientId },
    });

    this.activeVitals.set('pageLoad', reference);
  }

  pageLoadEnd(dateRange: DateRange, dataSize?: number, clientId?: string): void {
    const reference = this.activeVitals.get('pageLoad');
    if (reference) {
      const context = this.buildContext(dateRange, clientId, dataSize);
      datadogRum.stopDurationVital(reference, { context });
      this.activeVitals.delete('pageLoad');
    }
  }

  dataFetchStart(queryType: string, dateRange?: DateRange, clientId?: string): void {
    const context = this.buildContext(dateRange, clientId);
    const reference = datadogRum.startDurationVital('generalLedgerDataFetch', {
      description: queryType,
      context,
    });

    this.activeVitals.set(`dataFetch_${queryType}`, reference);
  }

  dataFetchEnd(
    queryType: string,
    dateRange: DateRange,
    dataSize?: number,
    clientId?: string,
  ): void {
    const reference = this.activeVitals.get(`dataFetch_${queryType}`);
    if (reference) {
      const context = this.buildContext(dateRange, clientId, dataSize);
      datadogRum.stopDurationVital(reference, { context });
      this.activeVitals.delete(`dataFetch_${queryType}`);
    }
  }

  dateRangeChangeStart(clientId?: string): void {
    const reference = datadogRum.startDurationVital('generalLedgerDateRangeChange', {
      description: 'user_initiated',
      context: { clientId },
    });

    this.activeVitals.set('dateRangeChange', reference);
  }

  dateRangeChangeEnd(dateRange: DateRange, dataSize?: number, clientId?: string): void {
    const reference = this.activeVitals.get('dateRangeChange');
    if (reference) {
      const context = this.buildContext(dateRange, clientId, dataSize);
      datadogRum.stopDurationVital(reference, { context });
      this.activeVitals.delete('dateRangeChange');
    }
  }

  trackDataProcessingDuration(
    processingType: string,
    startTime: number,
    endTime: number,
    dateRange: DateRange,
    dataSize?: number,
    clientId?: string,
  ): void {
    const duration = endTime - startTime;
    const context = this.buildContext(dateRange, clientId, dataSize);

    datadogRum.addDurationVital('generalLedgerDataProcessing', {
      startTime,
      duration,
      description: processingType,
      context,
    });
  }

  addCustomTiming(timingName: string, dateRange?: DateRange, clientId?: string): void {
    const fullTimingName = `generalLedger_${timingName}`;
    datadogRum.addTiming(fullTimingName);
  }

  trackComponentRender(
    componentName: string,
    dateRange?: DateRange,
    clientId?: string,
  ): VitalReference {
    const context = this.buildContext(dateRange, clientId);
    const reference = datadogRum.startDurationVital('generalLedgerComponentRender', {
      description: componentName,
      context,
    });

    return {
      stop: () => {
        const finalContext = this.buildContext(dateRange, clientId);
        datadogRum.stopDurationVital(reference, { context: finalContext });
      },
    };
  }

  trackUserWaitTime(
    waitType: string,
    duration: number,
    dateRange: DateRange,
    clientId?: string,
  ): void {
    const context = this.buildContext(dateRange, clientId);

    datadogRum.addDurationVital('generalLedgerUserWait', {
      startTime: Date.now() - duration,
      duration,
      description: waitType,
      context,
    });
  }
}

export const glPerformanceTracker = new GeneralLedgerPerformanceTracker();

export const useGeneralLedgerPerformanceTracking = () => {
  return glPerformanceTracker;
};
